import { addDays, format, parse } from "date-fns";
import Dexie from 'dexie';
import * as api from "../shared/api";
import * as base_url from "../shared/base_url";
import * as biz_data_helper from "../shared/biz_data_helper";

const new_expire_at = function (ttl = 90) {
  let today = parse(format(new Date(), "yyyy-MM-dd"), "yyyy-MM-dd", new Date())
  return format(addDays(today, ttl), "yyyy-MM-dd")
}

export class Database extends Dexie {
  constructor() {
    super('database');

    this.version(9).stores({
      cache: '++id,&key,expire_at',
      user_setting: '++id,&[user_idx+key]',
      plant_data_cache: '++id,&[plant_idx+key]',
      system_setting_cache: '++id'
    });

    this.cache = this.table('cache');
    this.user_setting = this.table('user_setting')
    this.plant_data_cache = this.table('plant_data_cache')
    this.system_setting_cache = this.table('system_setting_cache')
  }

  async getCache(key, version = "") {
    let record = await this.cache.where({ key }).first();
    if (record === undefined) return null

    if ((version || record.version) && version !== record.version) {
      return null
    } else if (record.expire_at) {
      let expire = parse(record.expire_at, "yyyy-MM-dd", new Date())
      let today = parse(format(new Date(), "yyyy-MM-dd"), "yyyy-MM-dd", new Date())
      if (expire < today) {
        return null
      }
    }

    return record.item
  }

  async setCache(key, item, expire_at, version = "") {
    if (!expire_at) {
      expire_at = new_expire_at()
    }
    let exists = await this.cache.where({ key }).first()
    if (exists) {
      return await this.cache.update(exists.id, { item, expire_at, version })
    } else {
      return await this.cache.put({ key, item, expire_at, version })
    }
  }

  async purgeCache(is_all = false) {
    if (is_all) {
      return await this.cache.clear()
    }
    let today = format(new Date(), "yyyy-MM-dd")
    return await this.cache.where("expire_at").below(today).delete();
  }

  async getUserSettings(user_idx) {
    return await this.user_setting.where({ user_idx }).toArray()
  }

  async getUserSetting(user_idx, key) {
    return await this.user_setting.where({ user_idx, key }).first()
  }

  async setUserSetting(user_idx, key, item) {
    let exists = await this.user_setting.where({ user_idx, key }).first()
    if (exists) {
      return await this.user_setting.update(exists.id, { item })

    } else {
      return await this.user_setting.put({ user_idx, key, item })

    }
  }

  async purgeUserSettings(user_idx) {
    return await this.user_setting.where({ user_idx }).clear()
  }

  async getPlantDataCache(plant_idx, key) {
    let version = ""
    let is_expired = false
    let record = null
    try {
      record = await this.plant_data_cache.where({ plant_idx, key }).first();
      version = record.version
    } catch (e) {
      console.log(e)
    }
    if (record && record.expire_at) {
      let expire = parse(record.expire_at, "yyyy-MM-dd", new Date())
      let today = parse(format(new Date(), "yyyy-MM-dd"), "yyyy-MM-dd", new Date())
      if (expire < today) {
        is_expired = true
      }
    }
    let result = await this.check_cache("plant_data", key, version, is_expired)
    if (result.key === key) {
      if (result.version !== version || is_expired || record === null) {
        //版本不同，或者已过期
        let expire_at = new_expire_at()
        if (result.expire_at) expire_at = result.expire_at
        await this.setPlantDataCache(plant_idx, key, result.item, expire_at, result.version)
        return result.item
      } else {
        return record.item
      }
    }
    return null
  }

  async setPlantDataCache(plant_idx, key, item, expire_at, version = "") {
    let exists = await this.plant_data_cache.where({ plant_idx, key }).first()
    if (exists) {
      return await this.plant_data_cache.update(exists.id, { item, expire_at, version })
    } else {
      return await this.plant_data_cache.put({ plant_idx, key, item, expire_at, version })

    }
  }

  async purgePlantDataCache(plant_idx, is_all = false) {
    if (is_all) {
      return await this.plant_data_cache.where({ plant_idx }).clear()
    }
    let today = format(new Date(), "yyyy-MM-dd")
    return await this.plant_data_cache.where({ plant_idx }).and((item) => item.expire_at < today).delete();
  }

  async check_cache(type, key, version, is_expired) {
    let result = await api.query_async({
      url: base_url.base_url + "/test/get_cache_data",
      parameters: { type, key, version, is_expired }
    })

    let data = biz_data_helper.extract_oject(result)
    return data
  }


  async getSystemSettingCache() {
    let version = ""
    let setting = null
    try {
      setting = (await this.system_setting_cache.toArray())[0];
      version = setting.version
    } catch (e) {
      console.log(e)
    }

    let result = await this.check_cache("system_setting", null, version, null)
    if (result.version !== version || setting === null) {
      //版本不同
      await this.setSystemSettingCache(result.item, result.version)
      return result.item
    } else {
      return setting.item
    }
  }

  async setSystemSettingCache(item, version = "") {
    let exists = await this.system_setting_cache.toArray()
    if (exists.length > 0) {
      return await this.system_setting_cache.update(exists[0].id, { item, version })
    } else {
      return await this.system_setting_cache.put({ item, version })
    }
  }
}
